import { useCreateEvent } from "@/features/events/hooks/useCreateEvent"
import { EventType as NewEventType } from "@/features/events/types/EventType"
import { Exam } from "../../../utils/api-objects/Exam"
import { saveEvent } from "../../../utils/apis/user-service"
import { EventType } from "../../../utils/enums/EventType"
import { handleDate } from "../../../utils/Functions"
import { useAuthStore } from "../../../utils/stores/useAuthStore"
import { useUserStore } from "../../../utils/stores/useUserStore"
import DownloadButton from "./DownloadButton"

export enum PdfType {
    DnaTest = "dna-test",
    BloodTest = "blood-test",
    CrossResults = "cross-results",
    HealthPlan = "health-plan",
    Diet = "diet",
    EatingHabits = "eating-habits",
    ShoppingList = "shopping-list",
    TipsAndRecepes = "tips-and-recepes",
    SeasonalFruitsAndVegetables = "seasonal-fruits-and-vegetables",
    Uploads = "uploads",
}

interface Props {
    exam: Exam,
    pdfType: PdfType,
}

export const PdfCard: React.FC<Props> = ({
    exam,
    pdfType,
}) => {
    const { sendEvent } = useCreateEvent()
    const { auth } = useAuthStore()
    const { userData } = useUserStore()
    const email = userData.email

    const handleEmoji = () => {
        switch (pdfType) {
            case PdfType.DnaTest:
                return "🧬"
            case PdfType.BloodTest:
                return "🩸"
            case PdfType.CrossResults:
                return "🔀"
            case PdfType.HealthPlan:
                return "🌀"
            case PdfType.Diet:
                return "🥙"
            case PdfType.ShoppingList:
                return "🛒"
            case PdfType.TipsAndRecepes:
                return "🧑‍🍳"
            case PdfType.EatingHabits:
                return "🍽"
            case PdfType.SeasonalFruitsAndVegetables:
                return "🍎"
            case PdfType.Uploads:
                return "🗂️"
        }
    }

    const handleFilename = () => {
        switch (pdfType) {
            case PdfType.CrossResults:
                return "CrossResults.pdf"
            case PdfType.HealthPlan:
                return "HealthPlan.pdf"
            case PdfType.Diet:
                return exam.filename
            case PdfType.ShoppingList:
                return "Lista della spesa.pdf"
            case PdfType.TipsAndRecepes:
                return "Tips e ricette.pdf"
            case PdfType.EatingHabits:
                return "Eating habits.pdf"
            case PdfType.SeasonalFruitsAndVegetables:
                return "Frutta e verdura.pdf"
            default:
                return exam.filename
        }
    }

    const handleEventType = () => {
        switch (pdfType) {
            case PdfType.DnaTest:
                return EventType.DnaTestDownloadButtonClicked
            case PdfType.BloodTest:
                return EventType.BloodTestDownloadButtonClicked
            case PdfType.CrossResults:
                return EventType.CrossResultsDownloadButtonClicked
            case PdfType.HealthPlan:
                return EventType.HealthPlanDownloadButtonClicked
            case PdfType.Diet:
                return EventType.DietDownloadButtonClicked
            case PdfType.EatingHabits:
                return EventType.EatingHabitsDownloadButtonClicked
            case PdfType.TipsAndRecepes:
                return EventType.TipsAndRecepesDownloadButtonClicked
            case PdfType.ShoppingList:
                return EventType.ShoppingListDownloadButtonClicked
            case PdfType.SeasonalFruitsAndVegetables:
                return EventType.SeasonalFruitsAndVegetablesDownloadButtonClicked
            case PdfType.Uploads:
                return EventType.UploadedForYouDownloadButtonClicked
        }
    }

    const handleSourcePage = () => {
        switch (pdfType) {
            case PdfType.DnaTest:
                return "dna_test_page"
            case PdfType.BloodTest:
                return "blood_test_page"
            case PdfType.CrossResults:
                return "results_and_progresses_page"
            case PdfType.HealthPlan:
                return "health_plan_page"
            case PdfType.Diet:
                return "diet_page"
            case PdfType.EatingHabits:
                return "eating_habits_page"
            case PdfType.TipsAndRecepes:
                return "tips_and_recepes_page"
            case PdfType.SeasonalFruitsAndVegetables:
                return "seasonal_fruits_and_vegetables_page"
            case PdfType.ShoppingList:
                return "shopping_list_page"
            case PdfType.Uploads:
                return "uploaded_for_you_page"
        }
    }

    const isPdf = exam.filename.toLowerCase().endsWith(".pdf");
    const isImage = /\.(jpg|jpeg|png)$/i.test(exam.filename.toLowerCase());  // Check for images

    const onDownloadClick = async () => {
        try {
            if (isPdf) {
                window.open(exam.url)
            } else {
                const response = await fetch(exam.url);
                if (!response.ok) throw new Error("Failed to download file");

                const blob = await response.blob();
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement("a");

                a.href = url;
                a.download = handleFilename(); // Preserve the correct filename
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                window.URL.revokeObjectURL(url);
            }

            if (email) {
                saveEvent(
                    email,
                    handleEventType(),
                    (success) => console.log(success),
                    (error) => console.log(error),
                    handleSourcePage()
                );
            }

            await sendEvent ({
                event: NewEventType.BUTTON_CLICK,
                sub_category: "download_button",
                event_metadata: { file_type: pdfType}
            })
        } catch (error) {
            console.error("Download failed", error);
        }
    };

    return (
        <div className="w-full h-[516px] rounded-[10px] relative shadow-platform flex-shrink-0 md:w-[362px] md:h-[527px]">
            {isPdf ? (
                <iframe
                    className="w-full h-[75%] border-none m-[0] p-[0] block"
                    src={exam.url + "#toolbar=0"}
                    title="PDF Viewer"
                />
            ) : isImage ? (
                <img
                    className="w-full h-[75%] object-contain bg-gray-200"
                    src={exam.url}
                    alt="Immagine non caricata"
                    onError={(e) => {
                        (e.target as HTMLImageElement).src = "/images/placeholders/jpg-placeholder.png";
                    }}
                />
            ) : (
                <div className="w-full h-[75%] flex items-center justify-center bg-gray-300">
                    <span className="text-gray-600">File non supportato</span>
                </div>
            )}
            <div className="w-full h-[25%] m-0 p-[20px] flex items-center justify-between">
                <div className="flex items-center gap-[10px] min-w-0 flex-1">
                    <div className="text-[24px] flex-shrink-0">{handleEmoji()}</div>
                    <div className="flex flex-col min-w-0 flex-1">
                        <div className="font-bold text-[16px] leading-[20px] truncate">
                            {handleFilename()}
                        </div>
                        <div className="font-normal text-[12px] leading-[16px] text-gray-500">
                            {handleDate(exam.uploaded_at)}
                        </div>
                    </div>
                </div>
                <div className="flex-shrink-0 ml-2">
                    <DownloadButton onClick={onDownloadClick} />
                </div>
            </div>
        </div>
    );
};